import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DataTable from '../../components/table/DataTable';
import CategoryMultiSelect from '../../components/CategoryMultiSelect';
import ActionMenu from '../../components/ui/ActionMenu';
import FormField from '../../components/ui/FormField';
import FormModal from '../../components/ui/FormModal';
import Modal from '../../components/ui/Modal';
import SearchBar from '../../components/ui/SearchBar';
import StatusMessage from '../../components/ui/StatusMessage';
import { useAuth } from '../../context/AuthContext';
import {
  createProduct,
  getProductDeletionImpact,
  getProducts,
  getCategories,
  getMixedProductCalculationModeLabel,
  getMixedProductCalculationModeRequiredInputs,
  getMixedProductRequiredInputLabel,
  MIXED_PRODUCT_CALCULATION_MODE_OPTIONS,
  MIXED_PRODUCT_REQUIRED_INPUT_OPTIONS,
  normalizeMixedProductCalculationMode,
  normalizeMixedProductRequiredInputs,
  normalizeVariantNames,
  permanentlyDeleteProduct,
  restoreProduct,
  softDeleteProduct,
} from '../../services/firestoreService';
import {
  buildLookupMap,
  getDisplayValues,
  normalizeIdArray,
} from '../../utils/collectionUtils';

const getCategoryNames = (product, categoryMap) =>
  getDisplayValues(product.categories, categoryMap);

const productTypeOptions = [
  { value: 'simple', label: 'Simple' },
  { value: 'variant', label: 'Has Variants' },
  { value: 'mixed', label: 'Mixed' },
];

const getProductTypeLabel = (type) =>
  productTypeOptions.find((option) => option.value === type)?.label || 'Simple';

const getCountLabel = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const toggleRequiredInput = (currentInputs, inputValue) => {
  const selectedInputs = normalizeMixedProductRequiredInputs(currentInputs);

  return selectedInputs.includes(inputValue)
    ? selectedInputs.filter((item) => item !== inputValue)
    : [...selectedInputs, inputValue];
};

export default function ProductsList({ viewMode = 'active' }) {
  const productFields = [
    { name: 'name', label: 'Product Name', required: true },
    { name: 'description', label: 'Description', type: 'textarea', required: false },
  ];

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [restoreId, setRestoreId] = useState(null);
  const [deletePermanentId, setDeletePermanentId] = useState(null);
  const [permanentDeleteImpact, setPermanentDeleteImpact] = useState(null);
  const [permanentDeleteImpactLoading, setPermanentDeleteImpactLoading] = useState(false);
  const [permanentDeleteImpactError, setPermanentDeleteImpactError] = useState('');
  const [formData, setFormData] = useState({});
  const [categoryMap, setCategoryMap] = useState({});
  const [pageError, setPageError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [isPermanentlyDeletingProduct, setIsPermanentlyDeletingProduct] =
    useState(false);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  useEffect(() => {
    const fetchProductsAndCategories = async () => {
      setLoading(true);
      setPageError('');

      try {
        const [productData, categories] = await Promise.all([
          getProducts(),
          getCategories(),
        ]);

        setProducts(Array.isArray(productData) ? productData : []);
        setCategoryMap(buildLookupMap(categories, 'name'));
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
        setCategoryMap({});
        setPageError('Failed to load products.');
      } finally {
        setLoading(false);
      }
    };

    fetchProductsAndCategories();
  }, []);

  const handleOpenDeleteModal = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleOpenAddModal = () => {
    setFormData({
      categories: [],
      type: 'simple',
      allowedVariants: [],
      variantDraft: '',
      requiredInputs: [],
      calculationMode: 'fixed',
    });
    setFormError('');
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    if (isSubmitting) {
      return;
    }

    setIsAddModalOpen(false);
    setFormData({});
    setFormError('');
  };

  const handleAddAllowedVariant = () => {
    const nextVariant = String(formData.variantDraft || '').trim();

    if (!nextVariant) {
      setFormError('Variant name is required.');
      return;
    }

    const currentVariants = normalizeVariantNames(formData.allowedVariants);

    if (currentVariants.includes(nextVariant)) {
      setFormError('Variant already exists.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      allowedVariants: [...currentVariants, nextVariant],
      variantDraft: '',
    }));
    setFormError('');
  };

  const handleRemoveAllowedVariant = (variantName) => {
    setFormData((prev) => ({
      ...prev,
      allowedVariants: normalizeVariantNames(prev.allowedVariants).filter(
        (item) => item !== variantName
      ),
    }));
    setFormError('');
  };

  const handleCloseDeleteModal = () => {
    if (isDeletingProduct) {
      return;
    }

    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const updateProductStatus = (id, updates) => {
    setProducts((prev) =>
      prev.map((product) =>
        product.id === id ? { ...product, ...updates } : product
      )
    );
  };

  const handleConfirmDelete = async () => {
    if (isDeletingProduct) {
      return;
    }

    try {
      if (!selectedProduct?.id) {
        return;
      }

      setIsDeletingProduct(true);
      await softDeleteProduct(selectedProduct.id);

      updateProductStatus(selectedProduct.id, {
        isDeleted: true,
        deletedAt: new Date(),
      });

      setPageMessage('Product moved to Trash.');
      setPageError('');
      setIsModalOpen(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('DELETE ERROR:', error);
      setPageError('Failed to delete product.');
    } finally {
      setIsDeletingProduct(false);
    }
  };

  const handleRestore = async () => {
    try {
      if (!restoreId) {
        return;
      }

      await restoreProduct(restoreId);

      updateProductStatus(restoreId, {
        isDeleted: false,
        deletedAt: null,
      });

      setPageMessage('Product restored.');
      setPageError('');
      setRestoreId(null);
    } catch (error) {
      console.error('RESTORE ERROR:', error);
      setPageError('Failed to restore product.');
    }
  };

  const handleOpenPermanentDeleteModal = async (product) => {
    setDeletePermanentId(product?.id || null);
    setPermanentDeleteImpact(null);
    setPermanentDeleteImpactError('');

    if (!product?.id) {
      return;
    }

    setPermanentDeleteImpactLoading(true);

    try {
      setPermanentDeleteImpact(await getProductDeletionImpact(product.id));
    } catch (error) {
      console.error('PERMANENT DELETE IMPACT ERROR:', error);
      setPermanentDeleteImpactError('Failed to load related record counts.');
    } finally {
      setPermanentDeleteImpactLoading(false);
    }
  };

  const handleClosePermanentDeleteModal = () => {
    if (isPermanentlyDeletingProduct) {
      return;
    }

    setDeletePermanentId(null);
    setPermanentDeleteImpact(null);
    setPermanentDeleteImpactLoading(false);
    setPermanentDeleteImpactError('');
  };

  const handlePermanentDelete = async (id) => {
    if (
      isPermanentlyDeletingProduct ||
      permanentDeleteImpactLoading ||
      permanentDeleteImpactError ||
      !permanentDeleteImpact
    ) {
      return;
    }

    try {
      setIsPermanentlyDeletingProduct(true);
      await permanentlyDeleteProduct(id);
      setProducts((prev) => prev.filter((product) => product.id !== id));
      setPageMessage('Product permanently deleted and related records deleted.');
      setPageError('');
      setDeletePermanentId(null);
      setPermanentDeleteImpact(null);
      setPermanentDeleteImpactLoading(false);
      setPermanentDeleteImpactError('');
    } catch (error) {
      console.error('PERMANENT DELETE ERROR:', error);
      setPageError('Failed to permanently delete product.');
    } finally {
      setIsPermanentlyDeletingProduct(false);
    }
  };

  const handleAddProduct = async (event) => {
    event.preventDefault();

    const missingField = productFields.find(
      (field) => field.required && !String(formData[field.name] || '').trim()
    );

    if (missingField) {
      setFormError(`${missingField.label} is required.`);
      return;
    }

    const productType = ['simple', 'variant', 'mixed'].includes(formData.type)
      ? formData.type
      : 'simple';
    const allowedVariants = normalizeVariantNames(formData.allowedVariants);
    const requiredInputs =
      productType === 'mixed'
        ? normalizeMixedProductRequiredInputs(formData.requiredInputs)
        : [];
    const calculationMode =
      productType === 'mixed'
        ? normalizeMixedProductCalculationMode(formData.calculationMode)
        : 'fixed';
    const missingCalculationInputs =
      getMixedProductCalculationModeRequiredInputs(calculationMode).filter(
        (input) => !requiredInputs.includes(input)
      );

    if (productType === 'variant' && String(formData.variantDraft || '').trim()) {
      setFormError('Add or clear the pending variant name.');
      return;
    }

    if (productType === 'variant' && allowedVariants.length === 0) {
      setFormError('Add at least one allowed variant.');
      return;
    }

    if (productType === 'mixed' && missingCalculationInputs.length > 0) {
      setFormError(
        `${getMixedProductCalculationModeLabel(calculationMode)} requires ${missingCalculationInputs
          .map((input) => getMixedProductRequiredInputLabel(input))
          .join(', ')}.`
      );
      return;
    }

    const payload = {
      name: formData.name?.trim() || '',
      description: formData.description?.trim() || '',
      type: productType,
      allowedVariants,
      requiredInputs,
      calculationMode,
      categories: normalizeIdArray(formData.categories),
      isDeleted: false,
      deletedAt: null,
    };

    setFormError('');
    setIsSubmitting(true);

    try {
      const createdProduct = await createProduct(payload);

      setProducts((prev) => [...prev, createdProduct]);
      setPageMessage('Product created.');
      setPageError('');

      handleCloseAddModal();
    } catch (error) {
      console.error('ADD PRODUCT ERROR:', error);
      setFormError('Failed to add product.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const normalizedProducts = products.map((product) => ({
    ...product,
    type: ['simple', 'variant', 'mixed'].includes(product.type)
      ? product.type
      : 'simple',
    categories: normalizeIdArray(product.categories),
  }));

  const filteredProducts = normalizedProducts
    .filter((product) => {
      if (viewMode === 'active') return product.isDeleted !== true;
      if (viewMode === 'deleted') return product.isDeleted === true;
      return true;
    })
    .filter((product) => {
      const matchesSearch = (product.name || '')
        .toLowerCase()
        .includes(searchTerm.trim().toLowerCase());
      const matchesCategory =
        selectedCategories.length === 0 ||
        product.categories?.some((categoryId) =>
          selectedCategories.includes(categoryId)
        );

      return matchesSearch && matchesCategory;
    });

  const columns = [
    { key: 'name', label: 'Product' },
    { key: 'type', label: 'Type' },
    { key: 'category', label: 'Category' },
  ];

  if (isAdmin) {
    columns.push({ key: 'actions', label: '' });
  }

  const rowStyle = {
    cursor: 'default',
  };

  const productNameCellStyle = {
    color: '#0f172a',
  };

  const renderDeletionImpact = (impact, isLoading, errorMessage) => {
    if (isLoading) {
      return <p className="page-subtitle">Checking related records...</p>;
    }

    if (errorMessage) {
      return <StatusMessage tone="error" message={errorMessage} />;
    }

    if (!impact) {
      return null;
    }

    return (
      <div className="modal-description" style={{ marginTop: 12 }}>
        <p style={{ marginTop: 0 }}>This will also delete:</p>
        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
          <li>{getCountLabel(impact.supplierCount, 'affected supplier')}</li>
          <li>{getCountLabel(impact.priceCount, 'price record')}</li>
          <li>{getCountLabel(impact.productLinkCount, 'product link')}</li>
          <li>{getCountLabel(impact.variantRecordCount, 'variant pricing record')}</li>
          <li>{getCountLabel(impact.componentRecordCount || 0, 'component')}</li>
          <li>{getCountLabel(impact.componentSupplierCount || 0, 'component supplier link')}</li>
        </ul>
      </div>
    );
  };

  const actionsCellStyle = {
    width: 72,
    textAlign: 'right',
  };

  return (
    <div className="page page--compact-list">
      <div className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">
            Manage product records and quickly review category data.
          </p>
        </div>

        {isAdmin && (
          <div className="page-header__actions">
            <button
              type="button"
              className="button button-primary"
              onClick={handleOpenAddModal}
            >
              + Add Product
            </button>
          </div>
        )}
      </div>

      <StatusMessage tone="success" message={pageMessage} />
      <StatusMessage tone="error" message={pageError} />

      <section className="section">
        <div className="controls-bar">
          <div className="controls-bar__search">
            <SearchBar
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search products by name..."
            />
          </div>
          <div className="controls-bar__filter">
            <CategoryMultiSelect
              value={selectedCategories}
              onChange={setSelectedCategories}
              compact
            />
          </div>

          <div className="toggle-group">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className={viewMode === 'active' ? 'active' : ''}
            >
              Active
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={() => navigate('/products/trash')}
                className={viewMode === 'deleted' ? 'active' : ''}
              >
                Trash
              </button>
            )}
          </div>
        </div>

        {loading ? <p className="page-subtitle">Loading products...</p> : null}

        {!loading ? (
          <div className="table-wrapper list-table-wrapper">
            <DataTable
              columns={columns}
              data={filteredProducts}
              emptyMessage="No products found."
              renderRow={(product) => {
                const categoryNames = getCategoryNames(product, categoryMap);

                return (
                  <tr
                    key={product.id}
                    style={rowStyle}
                  >
                    <td style={productNameCellStyle}>
                      <button
                        type="button"
                        className="clickable-name"
                        onClick={() => navigate(`/products/${product.id}`)}
                      >
                        {product.name || '-'}
                      </button>
                    </td>
                    <td>
                      <span className="tag">{getProductTypeLabel(product.type)}</span>
                    </td>
                    <td>
                      <div className="tag-list">
                        {categoryNames.length > 0
                          ? categoryNames.map((categoryName) => (
                            <span
                              key={categoryName}
                              className="tag"
                            >
                              {categoryName}
                            </span>
                          ))
                          : '-'}
                      </div>
                    </td>
                    {isAdmin && (
                      <td
                        style={actionsCellStyle}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {viewMode === 'deleted' ? (
                          <div
                            className="row-actions trash-row-actions"
                          >
                            <button
                              type="button"
                              onClick={() => setRestoreId(product.id)}
                              className="btn btn-success"
                              style={{ padding: '8px 12px' }}
                            >
                              Restore
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenPermanentDeleteModal(product)}
                              className="btn btn-danger"
                              style={{ padding: '8px 12px' }}
                            >
                              Delete Permanently
                            </button>
                          </div>
                        ) : (
                          <ActionMenu
                            onEdit={() => navigate(`/products/${product.id}`)}
                            onDelete={() => handleOpenDeleteModal(product)}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                );
              }}
            />
          </div>
        ) : null}
      </section>

      {isAdmin && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Delete Product"
          description={`Are you sure you want to delete ${
            selectedProduct?.name || 'this product'
          }? You can restore it later from Trash.`}
          confirmLabel={isDeletingProduct ? 'Deleting...' : 'Delete'}
          confirmDisabled={isDeletingProduct}
          cancelDisabled={isDeletingProduct}
        />
      )}

      {isAdmin && (
        <FormModal
          isOpen={isAddModalOpen}
          onClose={handleCloseAddModal}
          onSubmit={handleAddProduct}
          title="Add Product"
          submitLabel={isSubmitting ? 'Saving...' : 'Save Product'}
          submitDisabled={isSubmitting}
          cancelDisabled={isSubmitting}
          errorMessage={formError}
        >
          {productFields.map((field) => (
            <FormField
              key={field.name}
              name={field.name}
              label={field.label}
              type={field.type}
              required={field.required}
              value={formData[field.name] || ''}
              onChange={(value) => {
                setFormData((prev) => ({ ...prev, [field.name]: value }));
                if (formError) {
                  setFormError('');
                }
              }}
            />
          ))}
          <div className="form-group">
            <label htmlFor="product-type">Product Type</label>
            <select
              id="product-type"
              name="type"
              value={formData.type || 'simple'}
              onChange={(event) => {
                setFormData((prev) => ({
                  ...prev,
                  type: event.target.value,
                  allowedVariants:
                    event.target.value === 'variant'
                      ? normalizeVariantNames(prev.allowedVariants)
                      : [],
                  requiredInputs:
                    event.target.value === 'mixed'
                      ? normalizeMixedProductRequiredInputs(prev.requiredInputs)
                      : [],
                  calculationMode:
                    event.target.value === 'mixed'
                      ? normalizeMixedProductCalculationMode(prev.calculationMode)
                      : 'fixed',
                  variantDraft: '',
                }));
                if (formError) {
                  setFormError('');
                }
              }}
            >
              {productTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {(formData.type || 'simple') === 'variant' ? (
            <div className="form-group">
              <label htmlFor="allowed-variant">Allowed Variants *</label>
              <div className="inline-input-action">
                <input
                  id="allowed-variant"
                  name="allowedVariant"
                  type="text"
                  value={formData.variantDraft || ''}
                  onChange={(event) => {
                    setFormData((prev) => ({
                      ...prev,
                      variantDraft: event.target.value,
                    }));
                    if (formError) {
                      setFormError('');
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddAllowedVariant();
                    }
                  }}
                />
                <button
                  type="button"
                  className="button"
                  onClick={handleAddAllowedVariant}
                >
                  Add
                </button>
              </div>
              <div className="tag-list" style={{ marginTop: 10 }}>
                {normalizeVariantNames(formData.allowedVariants).length > 0
                  ? normalizeVariantNames(formData.allowedVariants).map((variantName) => (
                    <span key={variantName} className="tag">
                      {variantName}
                      <button
                        type="button"
                        className="button"
                        onClick={() => handleRemoveAllowedVariant(variantName)}
                        style={{ marginLeft: 8, padding: '2px 6px' }}
                        aria-label={`Remove ${variantName}`}
                      >
                        Remove
                      </button>
                    </span>
                  ))
                  : <span className="page-subtitle">No allowed variants yet.</span>}
              </div>
            </div>
          ) : null}
          {(formData.type || 'simple') === 'mixed' ? (
            <>
              <div className="form-group">
                <label htmlFor="mixed-calculation-mode">Calculation Mode</label>
                <select
                  id="mixed-calculation-mode"
                  name="calculationMode"
                  value={normalizeMixedProductCalculationMode(formData.calculationMode)}
                  onChange={(event) => {
                    const nextCalculationMode =
                      normalizeMixedProductCalculationMode(event.target.value);
                    const requiredModeInputs =
                      getMixedProductCalculationModeRequiredInputs(nextCalculationMode);

                    setFormData((prev) => ({
                      ...prev,
                      calculationMode: nextCalculationMode,
                      requiredInputs: Array.from(
                        new Set([
                          ...normalizeMixedProductRequiredInputs(prev.requiredInputs),
                          ...requiredModeInputs,
                        ])
                      ),
                    }));
                    if (formError) {
                      setFormError('');
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {MIXED_PRODUCT_CALCULATION_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Required Inputs</label>
                <div className="tag-list" style={{ alignItems: 'stretch' }}>
                  {MIXED_PRODUCT_REQUIRED_INPUT_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className="tag"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        checked={normalizeMixedProductRequiredInputs(
                          formData.requiredInputs
                        ).includes(option.value)}
                        onChange={() => {
                          setFormData((prev) => ({
                            ...prev,
                            requiredInputs: toggleRequiredInput(
                              prev.requiredInputs,
                              option.value
                            ),
                          }));
                          if (formError) {
                            setFormError('');
                          }
                        }}
                        disabled={isSubmitting}
                      />
                      {getMixedProductRequiredInputLabel(option.value)}
                    </label>
                  ))}
                </div>
              </div>
            </>
          ) : null}
          <div className="form-group">
            <label>Categories</label>
            <CategoryMultiSelect
              value={normalizeIdArray(formData.categories)}
              onChange={(newCategories) =>
                {
                  setFormData((prev) => ({
                    ...prev,
                    categories: normalizeIdArray(newCategories),
                  }));
                  if (formError) {
                    setFormError('');
                  }
                }
              }
            />
          </div>
        </FormModal>
      )}

      {isAdmin && (
        <Modal
          isOpen={Boolean(restoreId)}
          onClose={() => setRestoreId(null)}
          onConfirm={handleRestore}
          title="Restore Product"
          description="Are you sure you want to restore this product?"
        />
      )}

      {isAdmin && (
        <Modal
          isOpen={Boolean(deletePermanentId)}
          onClose={handleClosePermanentDeleteModal}
          onConfirm={async () => {
            await handlePermanentDelete(deletePermanentId);
          }}
          title="Delete Permanently"
          description="This action cannot be undone. Are you sure you want to permanently delete this product?"
          confirmLabel={
            isPermanentlyDeletingProduct ? 'Deleting...' : 'Delete Permanently'
          }
          confirmDisabled={
            isPermanentlyDeletingProduct ||
            permanentDeleteImpactLoading ||
            Boolean(permanentDeleteImpactError) ||
            !permanentDeleteImpact
          }
          cancelDisabled={isPermanentlyDeletingProduct}
        >
          {renderDeletionImpact(
            permanentDeleteImpact,
            permanentDeleteImpactLoading,
            permanentDeleteImpactError
          )}
        </Modal>
      )}
    </div>
  );
}
